import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Users, X, RefreshCw, Calendar, DollarSign } from 'lucide-react';
import apiService from '../../services/api';
import BaseModal from '../common/BaseModal';
import { formatDate } from '../../utils/dateUtils';

const BulkCategoryUpdateModal = ({
  isOpen,
  onClose,
  onConfirm,
  accountId,
  description,
  newCategory,
  newCategoryIcon,
  categoryLabel
}) => {
  const [transactions, setTransactions] = useState([]);
  const [selectedTransactionIds, setSelectedTransactionIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const loadSimilarTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getSimilarTransactions(accountId, description);
      setTransactions(data.transactions || []);
      // Initially select ALL transactions (cross-account bulk updates are now supported)
      setSelectedTransactionIds(new Set(data.transactions?.map(t => t.id) || []));
    } catch (err) {
      console.error('Error loading similar transactions:', err);
      setTransactions([]);
      setSelectedTransactionIds(new Set());
    } finally {
      setLoading(false);
    }
  }, [accountId, description]);

  useEffect(() => {
    if (isOpen && accountId && description) {
      loadSimilarTransactions();
    }
  }, [isOpen, accountId, description, loadSimilarTransactions]);

  const handleTransactionToggle = (transactionId) => {
    const newSelected = new Set(selectedTransactionIds);
    if (newSelected.has(transactionId)) {
      newSelected.delete(transactionId);
    } else {
      newSelected.add(transactionId);
    }
    setSelectedTransactionIds(newSelected);
  };

  const handleSelectAll = () => {
    setSelectedTransactionIds(new Set(transactions.map(t => t.id)));
  };

  const handleSelectNone = () => {
    setSelectedTransactionIds(new Set());
  };

  const handleConfirmUpdate = async (updateAll = false) => {
    try {
      setUpdating(true);
      if (updateAll) {
        // Update selected transactions only
        await onConfirm(Array.from(selectedTransactionIds));
      } else {
        // Update only the original transaction
        await onConfirm(false);
      }
    } catch (err) {
      console.error('Error updating transactions:', err);
      // Error will be handled by parent component
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = () => {
    if (!updating) {
      onClose();
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // formatDate imported from utils/dateUtils

  const selectedCount = selectedTransactionIds.size;
  const selectedFromOtherAccounts = transactions.filter(t =>
    t.account_id !== accountId && selectedTransactionIds.has(t.id)
  ).length;

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={updating ? () => {} : handleCancel}
      size="xl"
      showCloseButton={false}
      closeOnBackdrop={!updating}
    >
      {/* Custom Header with X button */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div className="flex items-center">
          <AlertTriangle className="h-6 w-6 text-orange-500 dark:text-orange-400 mr-3" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Bulk Category Update</h3>
        </div>
        <button
          onClick={handleCancel}
          disabled={updating}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 transition-colors"
          aria-label="Close modal"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-6">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              You're changing the category for transactions with this description:
            </p>

            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
              <p className="font-medium text-gray-900 dark:text-white mb-2">"{description}"</p>
              <div className="flex items-center text-sm">
                <span className="text-gray-500 dark:text-gray-400 mr-2">New category:</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                  <span className="mr-1">{newCategoryIcon}</span>
                  {categoryLabel}
                </span>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-gray-400 dark:text-gray-500 mr-3" />
                <span className="text-gray-600 dark:text-gray-400">Loading similar transactions...</span>
              </div>
            ) : transactions.length > 1 ? (
              <div>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <Users className="h-5 w-5 text-blue-500 dark:text-blue-400 mr-2" />
                      <span className="font-medium text-blue-900 dark:text-blue-200">
                        {transactions.length} similar transactions found
                      </span>
                    </div>
                    <div className="text-sm text-blue-700 dark:text-blue-300">
                      {selectedCount} of {transactions.length} selected
                      {selectedFromOtherAccounts > 0 && (
                        <span className="text-orange-600 dark:text-orange-400 ml-2">
                          (including {selectedFromOtherAccounts} from other accounts)
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
                    Review the transactions below and uncheck any that shouldn't be updated:
                  </p>

                  {/* Select All/None Controls */}
                  <div className="flex space-x-3">
                    <button
                      onClick={handleSelectAll}
                      className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
                    >
                      Select All
                    </button>
                    <button
                      onClick={handleSelectNone}
                      className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
                    >
                      Select None
                    </button>
                  </div>
                </div>

                {/* Transactions List */}
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-64 overflow-y-auto">
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {transactions.map((transaction) => (
                      <div
                        key={transaction.id}
                        className={`p-3 flex items-center space-x-3 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                          selectedTransactionIds.has(transaction.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedTransactionIds.has(transaction.id)}
                          onChange={() => handleTransactionToggle(transaction.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                <Calendar className="h-4 w-4 mr-1" />
                                {formatDate(transaction.transaction_date)}
                              </div>
                              <div className="flex items-center text-sm">
                                <DollarSign className="h-4 w-4 mr-1" />
                                <span className={transaction.total_amount < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}>
                                  {formatCurrency(transaction.total_amount)}
                                </span>
                              </div>
                              {transaction.account_id !== accountId && (
                                <div className="flex items-center text-xs text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 px-2 py-1 rounded">
                                  📄 {transaction.account_name || `Account ${transaction.account_id}`}
                                </div>
                              )}
                            </div>

                            {transaction.ai_category && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                                <span className="mr-1">{transaction.category_icon || '💰'}</span>
                                {transaction.ai_category}
                              </span>
                            )}
                          </div>

                          <div className="mt-1">
                            <p className="text-sm text-gray-900 dark:text-gray-100 font-medium truncate">
                              {transaction.description}
                            </p>
                            {transaction.reference_id && (
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Ref: {transaction.reference_id}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  This is the only transaction with this description in this account.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-800">
          <div className="flex justify-end space-x-3">
            <button
              onClick={handleCancel}
              disabled={updating}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Cancel
            </button>

            {transactions.length > 1 ? (
              <>
                <button
                  onClick={() => handleConfirmUpdate(false)}
                  disabled={updating}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                >
                  Update Only Original
                </button>
                <button
                  onClick={() => handleConfirmUpdate(true)}
                  disabled={updating || selectedCount === 0}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-700 border border-transparent rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 flex items-center"
                >
                  {updating && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
                  Update Selected ({selectedCount})
                </button>
              </>
            ) : (
              <button
                onClick={() => handleConfirmUpdate(false)}
                disabled={updating}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-700 border border-transparent rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 flex items-center"
              >
                {updating && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
                Update Category
              </button>
            )}
          </div>
        </div>
    </BaseModal>
  );
};

export default BulkCategoryUpdateModal;